// src/_constants/market-calendar.ts
export type MarketSession = 'pre' | 'open' | 'post' | 'closed'

const FULL_HOLIDAYS = new Set<string>([
  // 2026
  '2026-01-01', // New Year's Day
  '2026-01-19', // MLK Day
  '2026-02-16', // Presidents' Day
  '2026-04-03', // Good Friday
  '2026-05-25', // Memorial Day
  '2026-06-19', // Juneteenth
  '2026-07-03', // Independence Day (observed; Jul 4 is Sat)
  '2026-09-07', // Labor Day
  '2026-11-26', // Thanksgiving
  '2026-12-25', // Christmas
  // 2027
  '2027-01-01',
  '2027-01-18',
  '2027-02-15',
  '2027-03-26',
  '2027-05-31',
  '2027-06-18',
  '2027-07-05',
  '2027-09-06',
  '2027-11-25',
  '2027-12-24'
])

const HALF_DAYS = new Set<string>([
  '2026-11-27',
  '2026-12-24',
  '2027-07-02',
  '2027-11-26'
])

const CALENDAR_END_YEAR = 2027

interface NYWallClock {
  year: number
  month: number
  day: number
  weekday: number
  hour: number
  minute: number
  isoDate: string
}

function nyWallClock(d: Date): NYWallClock {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short'
  })
  const parts = fmt.formatToParts(d).reduce<Record<string, string>>((acc, p) => {
    if (p.type !== 'literal') acc[p.type] = p.value
    return acc
  }, {})
  const year = parseInt(parts.year, 10)
  const month = parseInt(parts.month, 10)
  const day = parseInt(parts.day, 10)
  const hour = parseInt(parts.hour === '24' ? '00' : parts.hour, 10)
  const minute = parseInt(parts.minute, 10)
  const weekdayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6
  }
  const weekday = weekdayMap[parts.weekday] ?? 0
  const isoDate = `${parts.year}-${parts.month}-${parts.day}`
  return { year, month, day, weekday, hour, minute, isoDate }
}

function minutesSinceMidnight(hour: number, minute: number): number {
  return hour * 60 + minute
}

const PRE_OPEN  = minutesSinceMidnight(4, 0)
const REG_OPEN  = minutesSinceMidnight(9, 30)
const REG_CLOSE = minutesSinceMidnight(16, 0)
const POST_END  = minutesSinceMidnight(20, 0)
const HALF_REG_CLOSE = minutesSinceMidnight(13, 0)

export function getMarketSession(now: Date): MarketSession {
  const wc = nyWallClock(now)

  if (wc.year > CALENDAR_END_YEAR) return 'closed'

  if (wc.weekday === 0 || wc.weekday === 6) return 'closed'

  if (FULL_HOLIDAYS.has(wc.isoDate)) return 'closed'

  const mins = minutesSinceMidnight(wc.hour, wc.minute)
  const isHalfDay = HALF_DAYS.has(wc.isoDate)
  const regClose = isHalfDay ? HALF_REG_CLOSE : REG_CLOSE

  if (mins < PRE_OPEN) return 'closed'
  if (mins < REG_OPEN) return 'pre'
  if (mins < regClose) return 'open'
  if (mins < POST_END) return 'post'
  return 'closed'
}
